import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTranslations } from '@/i18n/use-translations';
import Modal from '@/components/common/modal';
import { Box, Button, CircularProgress, Divider, Stack, Typography } from '@mui/material';
import { CheckCircleOutline, ContentCopy, InfoOutlined } from '@mui/icons-material';
import { useGenerateContributorInvitationLink } from '@/state/mutations/invitations/use-generate-contributor-invitation-link';
import { copyRichTextSync } from '@/helpers/clipboard.helper';
import type { ContributorDto } from '@/types/dtos/contributors/response/contributor.dto';
import type { FlowDto } from '@/types/dtos/flows/response/flow.dto';
import { useEventStore } from '@/state/stores/use-event-store';
import { useQuotaBalances } from '@/state/queries/entitlements/use-quota-balances';
import { resolveQuotaScopeForEvent } from '@/helpers/product-quota.helper';
import { PRODUCT_KEYS } from '@/types/dtos/orders/response/product.dto';
import EventCatalogProduct from '@/components/events/event-catalog/event-catalog-product';
import { buildInviteModalPreview } from '@/components/events/detail/tabs/contributors/invite-modal-preview.helpers';
import type { InvitePublicationUiContext } from '@/utils/contributor-invite-publication.utils';

type CopyState = 'idle' | 'loading' | 'generated' | 'copied' | 'error';

const CopyInviteLinkModal = ({
  open,
  onClose,
  contributor,
  eventId,
  contributors,
  flowContext,
}: {
  open: boolean;
  onClose: () => void;
  contributor: ContributorDto;
  eventId: string;
  contributors?: ContributorDto[];
  flowContext?: FlowDto;
}) => {
  const t = useTranslations();
  const { t: tI18n } = useTranslation();
  const [copyState, setCopyState] = useState<CopyState>('idle');
  const [generatedUrl, setGeneratedUrl] = useState<string | null>(null);
  const [clipboardFailCount, setClipboardFailCount] = useState(0);
  const generateLinkMutation = useGenerateContributorInvitationLink();
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const modalContentRef = useRef<HTMLDivElement>(null);

  const handleClose = useCallback(() => {
    setCopyState('idle');
    setGeneratedUrl(null);
    setClipboardFailCount(0);
    onClose();
  }, [onClose]);

  useEffect(() => {
    if (copyState === 'copied') {
      closeTimerRef.current = setTimeout(handleClose, 2000);
    }
    return () => clearTimeout(closeTimerRef.current);
  }, [copyState, handleClose]);

  const event = useEventStore((s) => s.event);
  const eventForSlots = useEventStore((s) => s.eventOrganize);
  const eventFlows = useEventStore((s) => s.eventFlowsOrganize);
  const eventPlan = useEventStore((s) => s.eventPlan);
  const eventFlowsPlan = useEventStore((s) => s.eventFlowsPlan);
  const eventSessionsPlan = useEventStore((s) => s.eventSessionsPlan);
  const eventFlowsOrganize = useEventStore((s) => s.eventFlowsOrganize);
  const eventSessionsOrganize = useEventStore((s) => s.eventSessionsOrganize);
  const { scopeType: quotaScopeType, scopeId: quotaScopeId } = resolveQuotaScopeForEvent(event);
  const { data: quotaBalances } = useQuotaBalances(quotaScopeType, quotaScopeId);

  const email = contributor.email?.trim();
  const isOptedOut = contributor.isOptedOut === true;
  const isFirstTimeInvite = !contributor.invitedAt;

  const invitePublicationCtx = useMemo(
    (): InvitePublicationUiContext | undefined =>
      eventPlan || eventFlowsPlan?.length || eventSessionsPlan?.length
        ? { eventPlan, eventFlowsPlan, eventSessionsPlan, eventFlowsOrganize, eventSessionsOrganize }
        : undefined,
    [eventPlan, eventFlowsPlan, eventSessionsPlan, eventFlowsOrganize, eventSessionsOrganize],
  );

  const liveInvitePreview = useMemo(() => {
    if (!contributors?.length) return [];
    return buildInviteModalPreview(
      contributors,
      flowContext,
      [contributor.id],
      eventForSlots,
      eventFlows,
      tI18n,
      invitePublicationCtx,
    );
  }, [contributors, flowContext, contributor.id, eventForSlots, eventFlows, tI18n, invitePublicationCtx]);

  const [snapshotPreview, setSnapshotPreview] = useState(liveInvitePreview);
  const invitePreview = copyState === 'idle' ? liveInvitePreview : snapshotPreview;

  const isEnoughCredits = useMemo(() => {
    if (!isFirstTimeInvite) return true;
    const row = quotaBalances?.find((balance) => balance.quotaKey === 'contributors');
    return (row?.available ?? 0) >= 1;
  }, [isFirstTimeInvite, quotaBalances]);

  const copyDisabled =
    !email || copyState === 'loading' || generateLinkMutation.isPending || (isFirstTimeInvite && !isEnoughCredits);

  const eventTitle = event?.title || '';

  const buildClipboardPlain = useCallback(
    (url: string) => {
      const e = (email ?? '').trim();
      const linkLabel = eventTitle
        ? t('contributor.copyInviteLinkModal.clipboardLinkLabel', { eventTitle })
        : t('contributor.copyInviteLinkModal.clipboardLinkLabelGeneric');
      const line1 = `${linkLabel}: ${url}`;
      if (!e) return line1;
      const noteKey = isOptedOut
        ? 'contributor.copyInviteLinkModal.clipboardInviteeNoteOptedOut'
        : 'contributor.copyInviteLinkModal.clipboardInviteeNote';
      const note = t(noteKey, { email: e });
      return `${line1}\n\n${note}`;
    },
    [email, eventTitle, isOptedOut, t],
  );

  const buildClipboardHtml = useCallback(
    (url: string) => {
      const e = (email ?? '').trim();
      const linkLabel = eventTitle
        ? t('contributor.copyInviteLinkModal.clipboardLinkLabel', { eventTitle })
        : t('contributor.copyInviteLinkModal.clipboardLinkLabelGeneric');
      const escaped = (s: string) =>
        s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
      const font = 'font-family:Arial,Helvetica,sans-serif;font-size:14px';
      const link = `<a href="${escaped(url)}" style="${font}">${escaped(linkLabel)}</a>`;
      if (!e) return `<div style="${font}">${link}</div>`;
      const noteKey = isOptedOut
        ? 'contributor.copyInviteLinkModal.clipboardInviteeNoteOptedOut'
        : 'contributor.copyInviteLinkModal.clipboardInviteeNote';
      const note = escaped(t(noteKey, { email: e }));
      return `<div style="${font}">${link}<br><br>${note}</div>`;
    },
    [email, eventTitle, isOptedOut, t],
  );

  const handleClipboardCopy = (url: string) => {
    const html = buildClipboardHtml(url);
    const container = modalContentRef.current ?? document.body;
    if (copyRichTextSync(html, container)) {
      setCopyState('copied');
      return;
    }
    setClipboardFailCount((c) => c + 1);
    setCopyState('generated');
  };

  /** Step 1: generate the link (async API call). Clipboard copy happens on a second click. */
  const handleGenerateLink = async () => {
    if (copyDisabled) return;
    setSnapshotPreview(liveInvitePreview);
    setGeneratedUrl(null);
    setClipboardFailCount(0);
    setCopyState('loading');
    try {
      const result = await generateLinkMutation.mutateAsync({
        eventId,
        contributorId: contributor.id,
      });
      setGeneratedUrl(result.url);
      setCopyState('generated');
    } catch {
      setCopyState('error');
    }
  };

  const description = !email
    ? t('contributor.copyInviteLinkModal.emailRequired')
    : isOptedOut
      ? t('contributor.copyInviteLinkModal.textOptedOut', { email })
      : t('contributor.copyInviteLinkModal.text', { email });

  return (
    <Modal
      open={open}
      handleClose={handleClose}
      size={invitePreview.length > 0 ? 'lg' : 'md'}
      title={t('contributor.copyInviteLinkModal.title')}
      description={description}
      footer={
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Button variant="outlined" color="info" onClick={handleClose}>
            {t('common.cancel')}
          </Button>
          {copyState === 'copied' ? (
            <Button
              variant="contained"
              color="success"
              startIcon={<CheckCircleOutline />}
              disabled
              sx={{
                '&.Mui-disabled': {
                  bgcolor: 'success.main',
                  color: 'success.contrastText',
                },
              }}
            >
              {t('contributor.copyInviteLinkModal.linkCopied')}
            </Button>
          ) : copyState === 'generated' && generatedUrl ? (
            <Button
              variant="contained"
              color="info"
              onClick={() => handleClipboardCopy(generatedUrl)}
              startIcon={<ContentCopy />}
            >
              {t('contributor.copyInviteLinkModal.copyLink')}
            </Button>
          ) : (
            <Button
              variant="contained"
              color="info"
              disabled={copyDisabled}
              onClick={handleGenerateLink}
              startIcon={copyState === 'loading' ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              {t('contributor.copyInviteLinkModal.generateLink')}
            </Button>
          )}
        </Box>
      }
    >
      <div ref={modalContentRef}>
        {invitePreview.length > 0 && (
          <Box sx={{ mb: 2 }} data-testid="copy-link-modal-preview">
            <Typography variant="subtitle2" gutterBottom>
              {t('contributor.inviteContributorModal.includedTitle')}
            </Typography>
            <Box
              sx={{
                maxHeight: 320,
                overflow: 'auto',
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                px: 1.5,
                py: 1,
              }}
            >
              <Stack spacing={2} divider={<Divider flexItem />}>
                {invitePreview.map((row) => (
                  <Stack key={row.contributorId} spacing={1}>
                    <Typography variant="body2" fontWeight={600}>
                      {row.displayName}
                    </Typography>
                    <Stack spacing={1.25} sx={{ pl: 0.5 }}>
                      {row.roles.map((rl, idx) => (
                        <Stack key={`${row.contributorId}-${idx}`} spacing={0.5}>
                          <Typography variant="body2" color="text.secondary">
                            {rl.roleHeadline}
                          </Typography>
                          {rl.slotLines.length > 0 && (
                            <Stack spacing={0.25} sx={{ pl: 1 }}>
                              {rl.slotLines.map((line, li) => (
                                <Typography
                                  key={`${row.contributorId}-${idx}-slot-${li}`}
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {line}
                                </Typography>
                              ))}
                            </Stack>
                          )}
                        </Stack>
                      ))}
                    </Stack>
                  </Stack>
                ))}
              </Stack>
            </Box>
          </Box>
        )}
        {isFirstTimeInvite && !isEnoughCredits && copyState === 'idle' && (
          <>
            <Box display="flex" alignItems="center" mb={2}>
              <InfoOutlined sx={{ mr: 1 }} />
              <Typography variant="body1">
                {t('contributor.inviteContributorModal.contributorsFeatureDescription')}
              </Typography>
            </Box>
            <EventCatalogProduct productKey={PRODUCT_KEYS.EVENT_CONTRIBUTOR} />
            <Divider sx={{ mt: 2 }} />
          </>
        )}
        {copyState === 'generated' && (
          <Stack spacing={1}>
            <Typography color="info.main" variant="body2">
              {t('contributor.copyInviteLinkModal.linkReady')}
            </Typography>
            {clipboardFailCount >= 2 && generatedUrl && (
              <Typography
                variant="caption"
                component="pre"
                sx={{
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  userSelect: 'all',
                  cursor: 'text',
                  color: 'text.secondary',
                  fontFamily: 'inherit',
                }}
              >
                {buildClipboardPlain(generatedUrl)}
              </Typography>
            )}
          </Stack>
        )}
        {copyState === 'error' && (
          <Typography color="error" variant="body2">
            {t('contributor.copyInviteLinkModal.error')}
          </Typography>
        )}
      </div>
    </Modal>
  );
};

export default CopyInviteLinkModal;
